import type { ReactElement } from 'react';
import { useId } from 'react';
import { Button } from '@studio/design-system/button';
import * as Dialog from '@studio/design-system/dialog';
import { Icon } from '@studio/design-system/icon';
import { useTrialCameraLocked } from '#~/api/billing/camera-access.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { useTrialNoticeShown } from './use-trial-notice-shown.js';

export interface TrialNoticeCamera {
  readonly agentId: string;
  readonly deviceId: string;
  readonly name: string;
}

export interface StandardTrialExpiryNoticeProps {
  readonly roomId: string;
  readonly studioName: string;
  readonly cameras: readonly TrialNoticeCamera[];
  readonly onDismiss: () => void;
  readonly onUpgrade?: () => void;
  readonly onShown?: () => void;
  readonly onStayFree?: () => void;
}

const focusRing = 'focus-visible:ring-2 focus-visible:ring-primary/50';

/** Availability follows the existing authoritative camera gate, not online status. */
const TrialNoticeCameraRow = ({
  roomId,
  camera
}: {
  readonly roomId: string;
  readonly camera: TrialNoticeCamera;
}): ReactElement => {
  const locked = useTrialCameraLocked(roomId, camera.agentId, camera.deviceId);
  return (
    <li
      className={`border-base-400 flex items-center gap-12 border-b px-12 py-8 last:border-b-0 ${locked ? 'text-muted' : 'font-demibold text-light'}`}
      {...DataSTag(Sel.StandardTrialNotice, 'camera', `${camera.agentId}/${camera.deviceId}`)}
      data-camera-locked={locked ? 'true' : 'false'}
    >
      {locked ? (
        <Icon icon='lock' aria-hidden='true' className='text-sm' />
      ) : (
        <span aria-hidden='true' className='size-8 shrink-0 rounded-full bg-success' />
      )}
      <span className='min-w-0 break-words'>{camera.name}</span>
      <span className='sr-only'>{locked ? 'Locked on Free' : 'Available on Free'}</span>
    </li>
  );
};

/**
 * Approved expiry presentation. The lifecycle consumer owns authorization and
 * display receipts; mounting this component is not an acknowledgement.
 * Upgrade delegates to the existing picker, which owns purchase consent.
 */
export const StandardTrialExpiryNotice = ({
  roomId,
  studioName,
  cameras,
  onDismiss,
  onUpgrade,
  onShown,
  onStayFree
}: StandardTrialExpiryNoticeProps): ReactElement => {
  const descriptionId = useId();
  const { ref, reportVisible } = useTrialNoticeShown(onShown);
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          reportVisible();
          onDismiss();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          ref={ref}
          data-trial-notice
          closeOnEscape
          aria-describedby={descriptionId}
          className='border-base-400 fixed left-1/2 top-1/2 max-h-[calc(100dvh_-_32px)] w-[calc(100vw_-_32px)] max-w-[480px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-12 border p-24 shadow-lg'
          {...DataSTag(Sel.StandardTrialNotice, 'expiry', roomId)}
        >
          <div className='flex items-start gap-8'>
            <Dialog.Title className='grow'>Your free trial has ended</Dialog.Title>
            <Dialog.Dismiss
              aria-label='Close'
              className={focusRing}
              {...STagNew(Sel.StandardTrialNotice, 'dismiss', roomId)}
            />
          </div>
          <p id={descriptionId} className='mt-8 text-sm text-muted'>
            {studioName} is on the Free plan now, which includes one camera.
          </p>
          {!onUpgrade && (
            <p className='mt-8 text-sm text-muted'>
              Ask the Studio owner to upgrade to restore access to the other cameras.
            </p>
          )}
          {cameras.length > 0 && (
            <ul
              aria-label='Configured cameras'
              // Per keyboard-reachable-interactive-elements (ai.knowledge/global),
              // a long retained-camera list remains reachable and scrollable.
              tabIndex={0}
              className={`my-20 max-h-[min(40dvh,240px)] overflow-y-auto rounded-8 bg-base-100 ${focusRing}`}
            >
              {cameras.map((camera) => (
                <TrialNoticeCameraRow
                  key={`${camera.agentId}/${camera.deviceId}`}
                  roomId={roomId}
                  camera={camera}
                />
              ))}
            </ul>
          )}
          <div className='mt-20 flex flex-wrap gap-8'>
            <Dialog.Close
              onClick={onUpgrade ? onStayFree : undefined}
              variant='secondary'
              className={`min-w-[160px] grow ${focusRing}`}
              {...STagNew(
                Sel.StandardTrialNotice,
                onUpgrade ? 'stay-free' : 'close-expiry',
                roomId
              )}
            >
              {onUpgrade ? 'Stay on Free' : 'Close'}
            </Dialog.Close>
            {onUpgrade && (
              <Button
                variant='primary'
                size='lg'
                className={`min-w-[160px] grow ${focusRing}`}
                onClick={() => {
                  reportVisible();
                  onUpgrade();
                }}
                {...STagNew(Sel.StandardTrialNotice, 'upgrade', roomId)}
              >
                Upgrade plan
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
